// The device's own copy of the curated staple foods, as a pure module (food staples plan 4c, #5625).
//
// The staples live in `global_common`, the curated-content database that already syncs
// download-only to every device. This module reads them into memory once and hands that snapshot to
// the typing suggestions, the offline food search, the staple food read and the scanners, so all of
// those work with no network.
//
// EVERY RULE BELOW IS EXPLAINED IN `design-docs/food-staples.md`, "Plan 4c: staples on every
// device" — when it may read at all, why a failed sync round is not a reason to refuse, why the
// revisions are captured as strings, what is frozen and what is live, and what happens at sign-out.
// The comments here say what a rule IS and point there for why.
//
// WHY THIS IS A SEPARATE FILE FROM THE SERVICE. Everything here is node-loadable: no `@angular/core`
// import, and every side effect arrives through `StapleSnapshotDeps`. The client's unit tests are
// plain node tests and cannot load an Angular service, so a rule that lives inside an `@Injectable`
// class cannot be tested at all. `CommonDbService` and `openCommonDbAsync` are the same split.

import { Food, FoodHeader, FoodStatus, Nutrients } from '@cf-shared/data-model/food';
import { isRecipe } from '@cf-shared/data-model/recipe';
import { buildFoodHeader } from 'services/food-header-builder';
import { analyzeFoodText, createFoodWordStemmer } from '@cf-shared/food-search/food-search-match';
import { StapleNames } from 'services/staple-suggestions';
import { transliterate } from 'transliteration';
import { FormatNutrients } from 'util/formatting';

/** the id prefix every staple food document has, and the key range the snapshot read asks for */
export const STAPLE_ID_PREFIX = 'food/$STP-';

/**
 * How old a snapshot may be before a page opening rebuilds it.
 *
 * A bound on staleness, not a tuned number: it is how long a correction can sit on the device
 * without the screen showing it, once the pull has landed it.
 */
export const STAPLE_SNAPSHOT_MAX_AGE_MS = 5 * 60 * 1000;

/**
 * How the staples' names and aliases are split and stemmed.
 *
 * OWNED HERE rather than taken as a parameter, and that is the point: the words are built once
 * and handed to every caller, so a parameter would have meant "used only if you happen to ask
 * first". Two callers already pass their own (the Foods page and the scanners), and a future
 * difference between them would have shown up only as foods quietly not matching.
 *
 * It is the same analyzer both of those use — `FoodWordCompleter.analyzerDeps` is built exactly
 * this way — so nothing about the matching changes.
 */
const stapleAnalyzer = { stem: createFoodWordStemmer(), foldAccents: transliterate };

/** the name this module reports a post-sync read failure under */
export const STAPLE_READ_FAILED_PROBLEM = 'StapleSnapshotReadFailed';

/** one staple's name words and alias words, already split and stemmed (Requirement D) */
export interface PreparedStapleWords {
  /**
   * The document these words were read from.
   *
   * Carried so a caller can check the words belong to the food in front of it, by identity.
   * The id is NOT enough: the person's own cached copy of a staple has the same id and can be
   * older, and so can the server's copy from `global_foods` — and scoring a row against a name
   * it does not have changes which foods the list shows, with nothing to say so.
   */
  doc: Food;
  /** the display name, analysed */
  name: string[];
  /** each alias, analysed, in the document's own order */
  aliases: string[][];
}

/** the part of `DatabaseWrapper` this module uses; narrowed so a test can stand in for it */
export interface StapleReadableDatabase {
  /** whether a read right now would cross the network instead of reading this device's store */
  readonly readsPassThroughToServer: boolean;
  /** `true` once a download succeeded, `false` when a round gave up, `undefined` before any finished */
  readonly initialSyncCompletedState: boolean | undefined;
  /** whether this device has ever finished a download of this database, in ANY session */
  readonly hasSyncedOnThisDeviceMarker: boolean;
  withDocWatcherAsync<T>(body: (watcher: unknown) => Promise<T>): Promise<T>;
  getByIdHeadAsync(headId: string, watcher: unknown): Promise<unknown[]>;
}

/** everything this module needs from the outside world */
export interface StapleSnapshotDeps {
  /** open the common database; must be awaited before `getDatabase` is asked */
  openCommonDbAsync: () => Promise<void>;
  /** the database to read from, or null when there is none */
  getDatabase: () => StapleReadableDatabase | null;
  /** report a read that failed although there was local data to read */
  reportProblem: (name: string, context: Record<string, unknown>) => void;
  /** the clock the age bound is measured against */
  nowMs: () => number;
}

/** what one snapshot holds, built once per read */
interface StapleSnapshot {
  /** every staple document, sorted by the numeric part of its id */
  docs: Food[];
  /** each staple's id, so `getStapleByIdAsync` does not walk the list */
  byId: Map<string, Food>;
  /**
   * Each staple's revision as a plain STRING, captured when the snapshot was built. Comparing
   * through the documents a later read returns cannot work — the design doc's "Detecting the change
   * needs the revisions captured as strings" says why, and there is a test that mutates a returned
   * object in place.
   */
  revisions: Map<string, string>;
  /** when this snapshot was built, for the age bound */
  builtAtMs: number;
  /** the rows the suggester takes, derived once */
  names: StapleNames[];
  /** the search headers, derived once */
  headers: FoodHeader[];
}

/**
 * Holds the snapshot and the rules for when it may be read and rebuilt.
 *
 * Every rule, and the reason for it, is in `design-docs/food-staples.md`, "Plan 4c: staples on every
 * device", under "Reading them on the device". In short: it reads only once there is local data
 * worth reading, it keeps one snapshot re-read at defined moments with no persistent watcher, a
 * failed read means "not ready" rather than "no staples", and a sign-out throws the snapshot away
 * even if a read is in flight.
 */
export class StapleSnapshotStore {
  private snapshot: StapleSnapshot | null = null;
  /** shared by concurrent callers, so overlapping requests cause ONE read */
  private readInFlight: Promise<StapleSnapshot | null> | null = null;
  /** the prepared words, derived from the snapshot on first request and kept with it */
  private preparedWords: Map<string, PreparedStapleWords> | null = null;
  /** a read that failed although there was local data is reported once, not on every retry */
  private reportedReadFailure = false;
  /**
   * Bumped by `reset()`. A build that finishes holding a stale generation is thrown away instead of
   * installed, and a caller is never handed an in-flight read from before the reset.
   *
   * A counter rather than comparing the database object, because a read already in flight when the
   * reset happens is holding the wrapper it started with — so comparing objects could not notice it,
   * however the next account's wrapper is obtained. See the design doc's "Sign-out".
   */
  private generation = 0;

  constructor(private deps: StapleSnapshotDeps) {
  }

  /**
   * The staple rows the typing suggester matches against; empty when the snapshot is not ready.
   *
   * Sorted by the NUMERIC part of the id, because `allDocs` answers in lexicographic order
   * (`food/$STP-1`, `-10`, `-100`, `-2`, ...) while `StapleSuggester` uses list position as its last
   * tie-break and the curated order is numeric. Without the sort the suggestion rows change.
   */
  async getStapleNamesAsync(): Promise<StapleNames[]> {
    const snapshot = await this.ensureSnapshotAsync();
    return snapshot ? snapshot.names : [];
  }

  /** one staple document, or null when the snapshot is not ready or does not hold that id */
  async getStapleByIdAsync(id: string): Promise<Food | null> {
    const snapshot = await this.ensureSnapshotAsync();
    return snapshot?.byId.get(id) ?? null;
  }

  /**
   * The name of the picture a staple shows, answered INSTANTLY from the snapshot already installed —
   * `undefined` when no snapshot is ready, when this id is not a staple, or when that staple carries
   * no picture (staple pictures plan, #5694).
   *
   * It never starts a read, and that is the point: Angular templates call this while rendering a row,
   * and a row of the typing suggestions carries only a staple id. A caller that wants the snapshot
   * built warms it as it already does — the Foods page calls `warmStapleSnapshotAsync` — and redraws
   * when that finishes.
   *
   * A damaged document is read as "no picture" rather than trusted: the field is optional and was
   * added long after the first staples were uploaded, so a document written by an older build has
   * none, and one with a non-string there is broken in a way nothing here should propagate.
   */
  pictureNameForId(foodId: string): string | undefined {
    const picture = this.snapshot?.byId.get(foodId)?.staple?.picture;
    return typeof picture === 'string' && picture !== '' ? picture : undefined;
  }

  /** every staple as a search header; empty when the snapshot is not ready */
  async getAllStapleHeadersAsync(): Promise<FoodHeader[]> {
    const snapshot = await this.ensureSnapshotAsync();
    return snapshot ? snapshot.headers : [];
  }

  /**
   * The staples' names and aliases already split and stemmed, so every staple name and alias is not
   * re-analysed on every offline search (Requirement D). Built once per snapshot and kept until the
   * snapshot is replaced; an unchanged re-read keeps them.
   *
   * Empty when the snapshot is not ready, which makes every caller behave exactly as it does today:
   * a header with no entry in this map is analysed the old way.
   *
   * The analyzer is this module's own — see `stapleAnalyzer` for why it is not a parameter.
   */
  async getPreparedWordsAsync(): Promise<Map<string, PreparedStapleWords>> {
    const snapshot = await this.ensureSnapshotAsync();
    if (!snapshot) return new Map();
    if (!this.preparedWords) {
      this.preparedWords = buildPreparedWords(snapshot.docs);
    }
    return this.preparedWords;
  }

  /**
   * Build the snapshot now if it is missing or past the age bound, and answer whether there is one.
   *
   * Pages call this when they open, because the row function the typing suggestions use has to
   * answer SYNCHRONOUSLY and so cannot wait for a read of its own.
   */
  async warmAsync(): Promise<boolean> {
    return (await this.ensureSnapshotAsync()) !== null;
  }

  /**
   * Throw the snapshot away. Sign-out calls this, so one account's staples can never be read by the
   * next — including when a read is still in flight, which the generation bump invalidates.
   */
  reset(): void {
    this.generation += 1;
    this.snapshot = null;
    this.readInFlight = null;
    this.preparedWords = null;
    this.reportedReadFailure = false;
  }

  /**
   * The snapshot, rebuilt when it is missing or stale; null when it cannot be built yet.
   *
   * Concurrent callers share one in-flight read: without that, a page opening while a search is
   * already warming would start a second read of the whole staple set.
   */
  private async ensureSnapshotAsync(): Promise<StapleSnapshot | null> {
    if (this.readInFlight) {
      // the same fallback the starting caller gets below: a caller that JOINS a rebuild must
      // not be told there are no staples when a refused rebuild left good ones in memory.
      // Without it, two callers in the same instant get two different answers.
      const shared = await this.readInFlight;
      return shared ?? this.snapshot;
    }
    if (this.snapshot && !this.snapshotIsStale()) {
      return this.snapshot;
    }
    const readForGeneration = this.generation;
    this.readInFlight = this.buildSnapshotAsync(readForGeneration);
    try {
      const rebuilt = await this.readInFlight;
      // A REBUILD THAT COULD NOT RUN MUST NOT THROW AWAY A SNAPSHOT WE ALREADY HOLD. The
      // refusal is about reading again, not about what we already read. The case that makes
      // this matter: an offline boot builds the snapshot, then the network comes back, the
      // wrapper builds a remote handle and re-arms its sync state, and for as long as that
      // round runs the gate refuses — so past the age bound every caller would be told there
      // are no staples, and the suggestions would empty out, while good ones sat in memory.
      // The caller that JOINED this rebuild gets the same treatment, at the top of this method.
      // `reset()` clears the snapshot itself, so a sign-out cannot be served from here.
      return rebuilt ?? this.snapshot;
    } finally {
      // only clear it if a reset has not already replaced it; clearing unconditionally would wipe a
      // newer read that started after the reset
      if (readForGeneration === this.generation) {
        this.readInFlight = null;
      }
    }
  }

  /** a snapshot past its age bound is re-read when something next asks for it */
  private snapshotIsStale(): boolean {
    if (!this.snapshot) return true;
    return this.deps.nowMs() - this.snapshot.builtAtMs >= STAPLE_SNAPSHOT_MAX_AGE_MS;
  }

  private async buildSnapshotAsync(readForGeneration: number): Promise<StapleSnapshot | null> {
    let docs: Food[];
    try {
      // the open is INSIDE the try on purpose: it throws when the database cannot be opened, and
      // this module promises "not ready" rather than a rejected promise for every such failure
      await this.deps.openCommonDbAsync();
      const database = this.deps.getDatabase();
      if (!database || !this.mayReadFrom(database)) {
        return null;
      }
      // a one-off watcher, released even when the read throws; a key-range read, so no design
      // document view is needed
      const read = await database.withDocWatcherAsync(
        watcher => database.getByIdHeadAsync(STAPLE_ID_PREFIX, watcher));
      docs = toStapleDocs(read);
    } catch (error) {
      this.reportReadFailureOnce(error);
      return null;
    }

    // a sign-out landed while this read was running: the account it belongs to is gone, so the
    // result is thrown away rather than installed
    if (readForGeneration !== this.generation) {
      return null;
    }
    try {
      // INSIDE a try as well, and not by habit: the install maps every document through the two
      // row builders, so a document shaped in a way they cannot handle would throw HERE rather
      // than in the read above. Outside, that throw escaped as a rejected promise — which is
      // exactly what every method on this class promises never to make a caller handle, and what
      // a floating warm-up call would turn into an unhandled rejection.
      return this.installSnapshot(docs);
    } catch (error) {
      this.reportReadFailureOnce(error);
      return null;
    }
  }

  /**
   * Whether reading now is both safe and worth doing. Two separate questions, and getting
   * either one wrong is silent — the design doc's "When it may read at all" has the reasoning.
   *
   * SAFE: the read must not cross the network. A read taken while the database is still passing
   * through would pull the whole curated staple set over the network and graft it into local
   * storage while the user is waiting for the app to start. Only the wrapper can answer that,
   * because it depends on whether a remote handle exists as well as on the sync state.
   *
   * WORTH DOING: there has to be local data, or an empty answer would be cached as "there are no
   * staples" when it really means "this device has not downloaded them yet". The marker survives
   * restarts, which is what makes an offline boot on a device the user has used before work; a
   * sync that has just succeeded is the other way in, for a brand-new account's first boot.
   */
  private mayReadFrom(database: StapleReadableDatabase): boolean {
    if (database.readsPassThroughToServer) return false;
    return database.hasSyncedOnThisDeviceMarker || database.initialSyncCompletedState === true;
  }

  /**
   * Report a failed read once per snapshot.
   *
   * Reported at all because neither thing that can fail here is the ordinary offline case: the read
   * only runs once `mayReadFrom` has said there is local data to read, and the local store opens
   * with no network, so a failed open is abnormal too. Reported ONCE because every later call would
   * otherwise report it again. Nothing is cached either way, so a first failure never leaves the
   * session empty for good — the next call simply tries again.
   */
  private reportReadFailureOnce(error: unknown): void {
    if (this.reportedReadFailure) return;
    this.reportedReadFailure = true;
    this.deps.reportProblem(STAPLE_READ_FAILED_PROBLEM, { message: (error as Error)?.message });
  }

  /** put the documents just read into the snapshot, reusing the derived rows when nothing changed */
  private installSnapshot(docs: Food[]): StapleSnapshot {
    const sorted = sortedStapleDocs(docs);
    const revisions = new Map<string, string>();
    for (const doc of sorted) {
      revisions.set(doc._id, String((doc as { _rev?: string })._rev ?? ''));
    }

    // an unchanged re-read keeps the derived rows and the prepared words: rebuilding them costs a
    // pass over every name and alias, and nothing about them has changed
    if (this.snapshot && sameRevisions(this.snapshot.revisions, revisions)) {
      this.snapshot = { ...this.snapshot, builtAtMs: this.deps.nowMs() };
      return this.snapshot;
    }

    this.snapshot = {
      docs: sorted,
      byId: new Map(sorted.map(doc => [doc._id, doc])),
      revisions,
      builtAtMs: this.deps.nowMs(),
      names: sorted.map(toStapleNames),
      headers: sorted.map(stapleDeviceRow),
    };
    // the words belong to documents that are gone, so they are rebuilt on the next request
    this.preparedWords = null;
    this.reportedReadFailure = false;
    return this.snapshot;
  }
}

/**
 * The staple documents out of whatever the read answered.
 *
 * A read that answers something other than a list of documents is treated as no staples rather than
 * allowed to throw out of the caller: this module's whole contract is that it never makes a caller
 * handle a failure.
 */
function toStapleDocs(read: unknown): Food[] {
  if (!Array.isArray(read)) return [];
  return read.filter((doc): doc is Food => !!doc && typeof (doc as Food)._id === 'string');
}

/** the staple documents in the order the device uses them: by the NUMBER in the id */
export function sortedStapleDocs(docs: Food[]): Food[] {
  return [...docs].sort(byNumericStapleId);
}

/**
 * The suggester rows for a set of staple documents.
 *
 * Exported so a test can compare the rows the real documents give against the rows the list that
 * used to be compiled into the app held, using the SAME derivation the device uses — a test that
 * copied these few lines instead would drift from production silently and keep passing.
 */
export function stapleRowsFromDocuments(docs: Food[]): StapleNames[] {
  return sortedStapleDocs(docs).map(toStapleNames);
}

/** order two staples by the number in their id */
function byNumericStapleId(left: Food, right: Food): number {
  return stapleNumber(left._id) - stapleNumber(right._id);
}

/** the number in `food/$STP-<number>`; an id that does not carry one sorts last rather than throwing */
function stapleNumber(id: string): number {
  const digits = Number(String(id).slice(STAPLE_ID_PREFIX.length));
  return Number.isFinite(digits) ? digits : Number.MAX_SAFE_INTEGER;
}

/** whether two revision maps describe the same documents at the same revisions */
function sameRevisions(before: Map<string, string>, after: Map<string, string>): boolean {
  if (before.size !== after.size) return false;
  for (const [id, rev] of after) {
    if (before.get(id) !== rev) return false;
  }
  return true;
}

/**
 * The names a staple can be found by.
 *
 * Every field is normalised rather than trusted: a document may have been written by an older
 * version of the app, or be damaged. `altNames` is checked for being a list AND for holding strings,
 * because a string there would otherwise spread into one alias per character.
 */
function stapleAliases(doc: Food): string[] {
  if (!Array.isArray(doc.altNames)) return [];
  return doc.altNames.filter((alias): alias is string => typeof alias === 'string');
}

/** the staple's name, never undefined, so every derivation of one document agrees */
function stapleName(doc: Food): string {
  return typeof doc.name === 'string' ? doc.name : '';
}

/** one staple as the typing suggester wants it */
function toStapleNames(doc: Food): StapleNames {
  return {
    id: doc._id,
    name: stapleName(doc),
    aliases: stapleAliases(doc),
    // a staple uploaded before this plan has no `popularity`; a damaged one may have no block at all
    popularity: doc.staple?.popularity ?? 0,
    groupOrder: doc.staple?.groupOrder ?? 0,
  };
}

/**
 * One staple read off the DEVICE, as a row for a results list.
 *
 * EXPORTED because the Add Foods page builds the same row for a picked staple, and a second copy
 * of these rules drifted immediately: the first one passed `staple.status` straight through, and
 * NO staple document carries that field, so every row it built was left with no status at all.
 * One function, one set of guards.
 *
 * The shared fields come from `buildFoodHeader`, which exists so the places that turn a food into a
 * row cannot drift apart. Two fields are deliberately NOT set, and the design doc's "Reading them
 * on the device" does not repeat this because it is a fact about this one function:
 *
 *  - NO `score`. The ranking treats a header without one as a DEVICE food, which is what this is.
 *    That is what makes it drop the server's copy of the same id, and the tier still comes from the
 *    id, which gives the Staples tier either way.
 *  - NO `altNames`. That field means the names a SERVER hit carried — `FoodHeader.altNames` says
 *    "unset on foods read from the device, whose names are on `cachedFood`", logged meals aside — and the own-food
 *    search does not set it. Both readers cope: the ranking reads `cachedFood.altNames` first, and
 *    the scanner's name list falls back to it.
 *
 * `status` is worked out here rather than through `FoodDbService.getFoodStatus`, where that rule
 * lives, because `FoodDbService` reads the staple snapshot from plan step 7 onwards and depending on
 * it back would be a cycle. Every staple id starts `food/$`, which is the first thing that function
 * tests, so the answer is Official for all of them.
 */
export function stapleDeviceRow(doc: Food): FoodHeader {
  const header = buildFoodHeader(
    { _id: doc._id, name: stapleName(doc), nutrients: doc.nutrients },
    {
      // `nutrients` is optional on a stored food, and the formatter has no guard of its own — an
      // absent one would throw and take the whole snapshot down, not just this row
      formatNutrients: (nutrients: Nutrients) => FormatNutrients(nutrients ?? {}),
      status: doc.status || FoodStatus.Official,
      recipe: isRecipe(doc),
    });
  header.cachedFood = doc;
  header.liked = doc.local?.liked;
  return header;
}

/** split and stem every staple's name and each of its aliases, once */
function buildPreparedWords(docs: Food[]): Map<string, PreparedStapleWords> {
  const prepared = new Map<string, PreparedStapleWords>();
  for (const doc of docs) {
    prepared.set(doc._id, {
      doc,
      name: analyzeFoodText(stapleName(doc), stapleAnalyzer),
      aliases: stapleAliases(doc).map(alias => analyzeFoodText(alias, stapleAnalyzer)),
    });
  }
  return prepared;
}
